import React, { createContext, useContext, useState, useEffect } from 'react';

// Define valid view modes
type ViewMode = 'grid' | 'table' | 'list';
const VALID_VIEW_MODES: ViewMode[] = ['grid', 'table', 'list'];

// Helper function to create namespaced storage keys
const createStorageKeys = (namespace: string) => ({
  VIEW_MODE_STORAGE_KEY: `imageManagerViewMode_${namespace}`,
  TREE_VISIBLE_STORAGE_KEY: `imageManagerTreeVisible_${namespace}`,
  SELECTED_COLLECTION_STORAGE_KEY: `imageManagerSelectedCollection_${namespace}`,
});

type ImageManagerContextType = {
  isTreeVisible: boolean;
  setIsTreeVisible: (value: boolean) => void;
  isModifierKeyPressed: boolean;
  setIsModifierKeyPressed: (value: boolean) => void;
  viewMode: ViewMode;
  setViewMode: (value: ViewMode) => void;
  selectedCollection: string | null;
  setSelectedCollection: (value: string | null) => void;
};

const ImageManagerContext = createContext<ImageManagerContextType | null>(null);

interface ImageManagerProviderProps {
  children: React.ReactNode;
  namespace?: string;
}

export function ImageManagerProvider({ children, namespace = 'default' }: ImageManagerProviderProps) {
  const [isModifierKeyPressed, setIsModifierKeyPressed] = useState(false);
  const storageKeys = createStorageKeys(namespace);

  // Function to get initial state from localStorage or default
  const getInitialViewMode = (): ViewMode => {
    try {
      const storedMode = localStorage.getItem(storageKeys.VIEW_MODE_STORAGE_KEY);
      if (storedMode && VALID_VIEW_MODES.includes(storedMode as ViewMode)) {
        return storedMode as ViewMode;
      }
    } catch (error) {
      console.error('Error reading viewMode from localStorage:', error);
    }
    return 'grid'; // Default value
  };

  // --- Tree Visibility Persistence ---
  const getInitialTreeVisible = (): boolean => {
    try {
      const storedValue = localStorage.getItem(storageKeys.TREE_VISIBLE_STORAGE_KEY);
      // Check for explicit 'false' string, otherwise default to true if anything else is stored (or null)
      return storedValue === 'false' ? false : true; // Default to true if not explicitly false
    } catch (error) {
      console.error('Error reading isTreeVisible from localStorage:', error);
    }
    return true; // Default value if error or not set
  };

  // --- Selected Collection Persistence ---
  const getInitialSelectedCollection = (): string | null => {
    try {
      return localStorage.getItem(storageKeys.SELECTED_COLLECTION_STORAGE_KEY);
    } catch (error) {
      console.error('Error reading selectedCollection from localStorage:', error);
    }
    return null; // Default value if error or not set
  };

  const [isTreeVisible, setIsTreeVisibleInternal] = useState<boolean>(getInitialTreeVisible);
  const [viewMode, setViewModeInternal] = useState<ViewMode>(getInitialViewMode);
  const [selectedCollection, setSelectedCollectionInternal] = useState<string | null>(getInitialSelectedCollection);

  // Modifier key handling effect with focus loss protection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Alt' || e.key === 'Shift') {
        setIsModifierKeyPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Alt' || e.key === 'Shift') {
        setIsModifierKeyPressed(false);
      }
    };

    // Handle window blur to reset modifier state (fixes screenshot bug)
    const handleBlur = () => {
      setIsModifierKeyPressed(false);
    };

    // Handle visibility change to reset modifier state
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsModifierKeyPressed(false);
      }
    };

    // Additional safety: check actual modifier key state on mouse events
    const handleMouseEvent = (e: MouseEvent) => {
      const actuallyPressed = e.ctrlKey || e.altKey || e.shiftKey || e.metaKey;
      if (!actuallyPressed && isModifierKeyPressed) {
        setIsModifierKeyPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleBlur); // Reset on focus too
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('mousemove', handleMouseEvent);
    window.addEventListener('click', handleMouseEvent);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('mousemove', handleMouseEvent);
      window.removeEventListener('click', handleMouseEvent);
    };
  }, [isModifierKeyPressed]);

  // Function to update state and localStorage
  const setViewMode = (mode: ViewMode) => {
    try {
      localStorage.setItem(storageKeys.VIEW_MODE_STORAGE_KEY, mode);
    } catch (error) {
      console.error('Error writing viewMode to localStorage:', error);
    }
    setViewModeInternal(mode);
  };

  // Function to update tree visibility state and localStorage
  const setIsTreeVisible = (value: boolean) => {
    try {
      localStorage.setItem(storageKeys.TREE_VISIBLE_STORAGE_KEY, value.toString()); // Store as 'true' or 'false'
    } catch (error) {
      console.error('Error writing isTreeVisible to localStorage:', error);
    }
    setIsTreeVisibleInternal(value);
  };

  // Function to update selected collection state and localStorage
  const setSelectedCollection = (value: string | null) => {
    try {
      if (value === null) {
        localStorage.removeItem(storageKeys.SELECTED_COLLECTION_STORAGE_KEY);
      } else {
        localStorage.setItem(storageKeys.SELECTED_COLLECTION_STORAGE_KEY, value);
      }
    } catch (error) {
      console.error('Error writing selectedCollection to localStorage:', error);
    }
    setSelectedCollectionInternal(value);
  };

  // Memoize the context value
  const contextValue = React.useMemo(
    () => ({
      isTreeVisible,
      setIsTreeVisible,
      isModifierKeyPressed,
      setIsModifierKeyPressed,
      viewMode,
      setViewMode,
      selectedCollection,
      setSelectedCollection,
    }),
    [isTreeVisible, isModifierKeyPressed, viewMode, selectedCollection]
  );

  return <ImageManagerContext.Provider value={contextValue}>{children}</ImageManagerContext.Provider>;
}

export function useImageManager() {
  const context = useContext(ImageManagerContext);
  if (!context) {
    throw new Error('useImageManager must be used within an ImageManagerProvider');
  }
  return context;
}
